import { Component, effect, inject, Injector, OnInit } from '@angular/core';
import { AuthService } from '@auth0/auth0-angular';
import { CardModule } from 'primeng/card';
import { AuthStore } from './global/store/authentication/auth.store';
import { MaintenanceStore } from './global/store/maintenance/maintenance.store';
import { PageWrapperComponent } from './global/components/page-wrapper/page-wrapper.component';
import { ToastModule } from 'primeng/toast';
import { ActivatedRoute, Router } from '@angular/router';
import { ClientUrlBuilder, ClientUrls } from './routing/client-urls';
import { toObservable } from '@angular/core/rxjs-interop';
import { filter, of, switchMap, take, zip } from 'rxjs';
import { TranslocoService } from '@jsverse/transloco';
import { PrimeNG } from 'primeng/config';
import { primeTranslations } from './constants/prime-translations';
import { MsalService } from '@azure/msal-angular';

@Component({
  selector: 'app-root',
  imports: [CardModule, PageWrapperComponent, ToastModule],
  providers: [],
  template: `
    <head>
      <title>BLINDSPOT</title>
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
      <link
        href="https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap"
        rel="stylesheet"
      />
      <link
        href="https://api.mapbox.com/mapbox-gl-js/v2.6.1/mapbox-gl.css"
        rel="stylesheet"
      />
    </head>
    <p-toast />
    <blindspot-page-wrapper></blindspot-page-wrapper>
  `,
})
export class AppComponent implements OnInit {
  public auth = inject(AuthService);
  public store = inject(AuthStore);
  public router = inject(Router);
  public route = inject(ActivatedRoute);
  public maintenanceStore = inject(MaintenanceStore);
  private readonly msalService = inject(MsalService);

  public constructor(
    private injector: Injector,
    private transloco: TranslocoService,
    private prime: PrimeNG,
  ) {
    // TODO: TEMP: Currently we only offer dark mode. So, we set the darkmode selector globally here. If we implement a darkmode toggle, we need to move this to a dedicated service.
    const element = document.querySelector('html');
    element?.classList.toggle('dark');

    effect(() => {
      if (!this.maintenanceStore.isUnderMaintenance()) return;
      // An admin with the per-browser skip active keeps browsing the app.
      if (this.maintenanceStore.skipMaintenance()) return;
      const target = ClientUrlBuilder.build(ClientUrls.Maintenance);
      if (!this.router.url.startsWith(target)) {
        this.router.navigateByUrl(target);
      }
    });
  }

  private redirectingToLogin = false;

  public ngOnInit(): void {
    // Required for MSAL to process redirect responses on app load.
    this.msalService.handleRedirectObservable().subscribe();

    if (window.location.pathname !== '/') {
      this.store.setUserDesiredPath(
        window.location.pathname + window.location.search,
      );
    }

    // Send the user back to login on any Auth0 SDK error (e.g. an expired/invalid
    // refresh token), instead of leaving them stuck on the "trouble logging in" page.
    this.auth.error$.subscribe(() => {
      if (this.redirectingToLogin) return;
      this.redirectingToLogin = true;
      this.auth.loginWithRedirect();
    });

    this.auth.isAuthenticated$.subscribe((authenticated) => {
      console.log(authenticated);
      if (!authenticated) {
        const params = new URLSearchParams(window.location.search);
        const invitation = params.get('invitation');
        const organization = params.get('organization');

        const authorizationParams =
          invitation && organization ? { organization, invitation } : undefined;

        this.auth.loginWithRedirect({
          authorizationParams,
        });
      } else {
        this.store.getLoggedInUser();
        // Wait for loggedInUser to be fetched before redirecting
        toObservable(this.store.loggedInUser, {
          injector: this.injector,
        })
          .pipe(
            filter((user) => user !== undefined),
            take(1),
            switchMap((user) =>
              zip(of(user), this.transloco.load(user.preferredLanguage)),
            ),
          )
          .subscribe(([user]) => {
            this.transloco.setActiveLang(user.preferredLanguage);
            this.prime.setTranslation(
              primeTranslations[user.preferredLanguage] ||
                primeTranslations['en'],
            );

            const userDesiredPath = this.store.userDesiredPath();
            if (userDesiredPath) {
              this.store.setUserDesiredPath(undefined);
              this.router.navigateByUrl(userDesiredPath);
            } else {
              this.router.navigateByUrl(
                ClientUrlBuilder.build(ClientUrls.Planning),
              );
            }
          });
      }
    });
  }
  protected readonly translations = primeTranslations;
}
